import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Shield, AlertTriangle, FileText, CheckCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function DMCACompliance() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Shield, title: 'Safe Harbor Protection', description: 'Establish DMCA safe harbor to limit liability for user content.' },
    { icon: AlertTriangle, title: 'Takedown Services', description: 'File effective DMCA takedown notices for infringing content.' },
    { icon: FileText, title: 'Policy Development', description: 'Create compliant DMCA policies and procedures.' },
    { icon: CheckCircle, title: 'Agent Registration', description: 'Register your DMCA agent with the Copyright Office.' },
  ];

  const faqs = [
    { question: 'What is the DMCA?', answer: 'The Digital Millennium Copyright Act (DMCA) is a U.S. law that provides a legal framework for addressing online copyright infringement, including safe harbor protections for service providers and takedown procedures.' },
    { question: 'What is a DMCA takedown notice?', answer: 'A DMCA takedown notice is a formal request to an online service provider to remove content that infringes your copyright. The notice must meet specific legal requirements to be effective.' },
    { question: 'What is DMCA safe harbor?', answer: 'Safe harbor provisions protect service providers from liability for user-posted infringing content if they comply with DMCA requirements, including designating an agent and responding to takedown notices.' },
    { question: 'How do I file a DMCA takedown?', answer: 'You must send a written notice to the service provider\'s designated DMCA agent, including specific information about the infringed work, the infringing material, and a sworn statement.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1563986768609-322da13575f3?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>DMCA Act Compliance</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Ensure compliance with DMCA requirements and protect your online content.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our DMCA Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Professional DMCA compliance and enforcement services.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about DMCA compliance.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Stay DMCA Compliant Today</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Protect your content and ensure compliance with DMCA requirements.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="dmca_compliance" />
      <Footer />
    </div>
  );
}